// SearchableSelect — uniform popup-style searchable picker for library/master
// data (Item, Client, Vendor, Member, Ship-To, etc.).
//
// Key behaviour (per UX brief):
//   • Closed state shows a single-line trigger (the selected label + chevron).
//   • Clicking opens a popup directly below: a search input at the top, then a
//     scrollable list of matches. AT MOST 8 ROWS ARE VISIBLE — the rest are
//     reached via the vertical scrollbar.
//   • The search input filters the FULL master list (not just the visible 8).
//   • Arrow Up / Down navigate, Enter selects, Esc closes.
//   • Outside-click closes. Selecting a row closes and fires onChange.
//
// The popup is portaled to `document.body` (Sprint 2026-Feb-11c) so it always
// paints above any parent `overflow-hidden` / stacking context — critical for
// pickers hosted inside table cells (e.g. JV line items) and Shadcn Dialogs.
//
// Generic enough to drop into any module — callers provide either a pre-loaded
// `items` array or an `endpoint` (e.g. "/clients") that the picker fetches on
// first open (cached for the session). Custom `getLabel` / `getSubLabel` /
// `getSearchText` lets each call-site decide what to render and what to match.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { api } from "@/lib/api";

const ROW_H = 36; // px — keep in sync with the row className below

export function SearchableSelect({
  value,                // current selected primitive (id) or empty string
  onChange,             // (id, fullRow) => void
  items: itemsProp,     // optional pre-loaded array
  endpoint,             // optional GET endpoint to lazy-load on first open
  getKey = (r) => r.id,
  getLabel = (r) => r.name || r.label || r.code || "",
  getSubLabel,          // optional secondary line
  getSearchText,        // (row) => string to match against (defaults to label)
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  allowClear = true,
  disabled = false,
  required = false,
  className = "",
  inputClassName = "",
  testid,
  renderRow,            // optional custom row renderer ({row, active}) => JSX
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(itemsProp || []);
  const [loaded, setLoaded] = useState(!!itemsProp);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  // Sprint 2026-Feb-11c — portal popup coordinates in viewport space
  // (getBoundingClientRect() of the trigger). Position is recomputed
  // on open, scroll, and window resize so the popup always tracks
  // the trigger button precisely.
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Keep in sync with prop changes (parent may load async)
  useEffect(() => {
    if (itemsProp !== undefined) {
      setItems(itemsProp || []);
      setLoaded(true);
    }
  }, [itemsProp]);

  // Lazy-load via endpoint on first open
  useEffect(() => {
    if (!open || loaded || !endpoint) return;
    let alive = true;
    api.get(endpoint)
      .then((r) => {
        if (!alive) return;
        const data = r.data;
        setItems(Array.isArray(data) ? data : (data?.items || []));
        setLoaded(true);
      })
      .catch(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, [open, loaded, endpoint]);

  // Auto-focus search box on open + reset query
  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  // Outside-click closes the popup. Because the popup is portaled to
  // document.body, we need to check BOTH the trigger root AND the
  // popup DOM subtree (otherwise clicks inside the popup itself would
  // close it prematurely).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      const inRoot   = rootRef.current  && rootRef.current.contains(e.target);
      const inPopup  = popupRef.current && popupRef.current.contains(e.target);
      if (!inRoot && !inPopup) setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Sprint 2026-Feb-11c — Compute popup coordinates from the trigger's
  // bounding rect so the portaled popup lands precisely below (or
  // above, if the viewport bottom is too close) the trigger button.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const POPUP_MAX_H = 340;   // search + up to 8 rows ≈ 40 + 8*36
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const openUp = spaceBelow < POPUP_MAX_H && spaceAbove > spaceBelow;
      setPopupPos({
        top: openUp ? Math.max(8, r.top - POPUP_MAX_H - 4) : r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 280),
        openUp,
      });
    };
    compute();
    window.addEventListener("scroll", compute, true);   // capture — catches ancestor scrolls too
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  // Sprint 2026-Feb-11d — Prevent Radix Dialog's FocusScope from
  // yanking focus back into the DialogContent when the user clicks
  // into our portaled search input. Radix attaches a document-level
  // bubble-phase `focusin` + `focusout` listener that calls
  // `focusFirst()` whenever focus leaves the scope. We intercept
  // BOTH in capture phase and short-circuit propagation when the
  // event involves our popup:
  //   • focusin  — target is inside the popup → stop.
  //   • focusout — relatedTarget (destination) is inside the popup
  //                → stop. Without this, moving focus FROM the
  //                trigger TO the popup search input triggers a
  //                focusout on the trigger with relatedTarget =
  //                search input, and Radix's bubble listener yanks
  //                focus straight back to the trigger.
  useEffect(() => {
    if (!open) return;
    const stopIfPopupInvolved = (e) => {
      const popup = popupRef.current;
      if (!popup) return;
      const inside =
        popup.contains(e.target) ||
        (e.relatedTarget && popup.contains(e.relatedTarget));
      if (inside) e.stopImmediatePropagation();
    };
    document.addEventListener("focusin",  stopIfPopupInvolved, true);
    document.addEventListener("focusout", stopIfPopupInvolved, true);
    return () => {
      document.removeEventListener("focusin",  stopIfPopupInvolved, true);
      document.removeEventListener("focusout", stopIfPopupInvolved, true);
    };
  }, [open]);

  const selectedRow = useMemo(
    () => items.find((r) => String(getKey(r)) === String(value)),
    [items, value, getKey],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    const getText = getSearchText || ((r) => `${getLabel(r) || ""} ${getSubLabel ? getSubLabel(r) || "" : ""}`);
    return items.filter((r) => (getText(r) || "").toLowerCase().includes(needle));
  }, [items, q, getLabel, getSubLabel, getSearchText]);

  // Aug-2026 — render cap. Large masters (10k+ vendors) froze the popup
  // because every filtered row rendered as a DOM node. Only the first
  // MAX_RENDER matches are painted; search still runs on the FULL list.
  const MAX_RENDER = 300;
  const shown = useMemo(() => filtered.slice(0, MAX_RENDER), [filtered]);

  // Keep active row in view when scrolling via keyboard
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row-idx="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open, filtered.length]);

  const pick = (row) => {
    onChange?.(getKey(row), row);
    setOpen(false);
  };
  const clear = (e) => {
    e.stopPropagation();
    onChange?.("", null);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const row = shown[active];
      if (row) pick(row);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      data-testid={testid}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        data-testid={testid ? `${testid}-trigger` : undefined}
        className={`w-full text-left px-2 py-1.5 text-xs border border-slate-300 bg-white hover:border-[#F97316] focus:border-[#F97316] focus:outline-none flex items-center gap-2 ${disabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""} ${inputClassName}`}
      >
        <span title={selectedRow ? getLabel(selectedRow) : undefined} className={`flex-1 truncate ${selectedRow ? "text-slate-900" : "text-slate-400"}`}>
          {selectedRow ? getLabel(selectedRow) : placeholder}
        </span>
        {allowClear && selectedRow && !disabled && (
          <span role="button" onClick={clear} className="text-slate-400 hover:text-rose-600 p-0.5" title="Clear">
            <X size={12} />
          </span>
        )}
        <ChevronDown size={13} className="text-slate-400 shrink-0" />
      </button>

      {open && createPortal(
        <div
          ref={popupRef}
          data-testid={testid ? `${testid}-popup` : undefined}
          className="fixed z-[9999] bg-white border border-slate-300 shadow-lg"
          style={{
            top: `${popupPos.top}px`,
            left: `${popupPos.left}px`,
            width: `${popupPos.width}px`,
            minWidth: "280px",
            // Sprint 2026-Feb-11d — Radix Dialog / Shadcn sets
            // `pointer-events: none` on <body> while a modal is
            // open, which propagates to our portaled popup and
            // makes the search input + rows un-clickable. Force
            // `auto` back on so the popup receives clicks / focus
            // regardless of any ancestor scroll-lock.
            pointerEvents: "auto",
          }}
          onKeyDown={onKey}
          // Stop pointer events from bubbling to `document` so
          // Radix Dialog's `pointerDownOutside` handler doesn't
          // treat popup interactions as "click outside dialog"
          // and yank focus away.
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="relative border-b border-slate-200">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => { setQ(e.target.value); setActive(0); }}
              placeholder={searchPlaceholder}
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full pl-8 pr-2 py-2 text-xs bg-white focus:outline-none"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
              {filtered.length}/{items.length}
            </div>
          </div>

          {!loaded ? (
            <div className="px-3 py-6 text-xs text-slate-500 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-6 text-xs text-slate-500 text-center" data-testid={testid ? `${testid}-empty` : undefined}>
              {emptyText}
            </div>
          ) : (
            <div
              ref={listRef}
              className="overflow-y-auto"
              style={{ maxHeight: `${ROW_H * 8}px` }}  // exactly 8 rows visible — rest scroll
              data-testid={testid ? `${testid}-list` : undefined}
            >
              {shown.map((row, idx) => {
                const k = String(getKey(row));
                const isSelected = String(value) === k;
                const isActive = idx === active;
                return (
                  <button
                    key={k}
                    type="button"
                    data-row-idx={idx}
                    data-testid={testid ? `${testid}-row-${k}` : undefined}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => pick(row)}
                    style={{ minHeight: ROW_H }}
                    title={`${getLabel(row) || ""}${getSubLabel && getSubLabel(row) ? ` — ${getSubLabel(row)}` : ""}`}
                    className={`w-full text-left px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0 flex items-center gap-2 ${
                      isActive ? "bg-orange-50" : "hover:bg-slate-50"
                    } ${isSelected ? "font-bold" : ""}`}
                  >
                    {renderRow ? (
                      renderRow({ row, active: isActive, selected: isSelected })
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-slate-900">{getLabel(row)}</div>
                          {getSubLabel && (
                            <div className="truncate text-[10px] text-slate-500">{getSubLabel(row)}</div>
                          )}
                        </div>
                        {isSelected && <Check size={12} className="text-emerald-600 shrink-0" />}
                      </>
                    )}
                  </button>
                );
              })}
              {filtered.length > shown.length && (
                <div className="px-3 py-1.5 text-[10px] text-slate-400 bg-slate-50 sticky bottom-0" data-testid={testid ? `${testid}-more-hint` : undefined}>
                  Showing first {shown.length} of {filtered.length} — keep typing to narrow
                </div>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
      {required && !selectedRow && (
        <input type="text" required value="" onChange={() => {}} className="sr-only absolute" tabIndex={-1} aria-hidden />
      )}
    </div>
  );
}
